import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Textarea, Button, Input, ColorPicker, SearchableSelect } from '@/components/ui';
import type { SearchableOption } from '@/components/ui';
import { Flag } from '@/components/ui';
import { uploadService } from '@/services';
import { X, Upload } from 'lucide-react';
import { step2Schema, type Step2Data as FormData } from '@/validations';
import * as ct from 'countries-and-timezones';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';

interface Country {
  id: string;
  name: string;
  timezones: string[];
}

interface Step2CompanyProps {
  initialData?: Partial<FormData>;
  onNext: (data: FormData) => void;
  onBack?: () => void;
  isSubmitting?: boolean;
  skipReset?: boolean;
}

export const Step2Company: FC<Step2CompanyProps> = ({
  initialData,
  onNext,
  onBack,
  isSubmitting = false,
  skipReset = false,
}) => {
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>(initialData?.logoUrl || '');
  const [logoPermanentUrl, setLogoPermanentUrl] = useState<string>(''); // Store permanent URL (S3 key) for backend

  // Utility function to extract S3 key/path from a URL
  const extractPathFromUrl = (url: string): string => {
    if (!url) return '';
    // If it's not a full URL (http/https), return as-is (it's already a path)
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return url;
    }
    try {
      const urlObj = new URL(url);
      // Extract pathname and remove leading slash, decode URI encoding
      const path = decodeURIComponent(urlObj.pathname.replace(/^\//, ''));
      // Remove query parameters (signed URLs have query params)
      return path.split('?')[0];
    } catch {
      // If URL parsing fails, return original value
      return url;
    }
  };
  const [countries, setCountries] = useState<SearchableOption[]>([]);
  const [selectedTimezone, setSelectedTimezone] = useState<string>(initialData?.timezone || '');

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
    reset,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(step2Schema),
    mode: 'onChange',
    defaultValues: {
      keyPeople: initialData?.keyPeople || [],
      brandColors: initialData?.brandColors || { primary: '', secondary: '', accent: '' },
      logoUrl: initialData?.logoUrl || '',
      brandGuidelines: initialData?.brandGuidelines || '',
      country: initialData?.country || '',
      countryCode: initialData?.countryCode || '',
      timezone: initialData?.timezone || '',
    },
  });

  // Load countries on mount
  useEffect(() => {
    const allCountries = ct.getAllCountries();
    const countryOptions: SearchableOption[] = Object.values(allCountries)
      .map((country: Country) => ({
        value: country.name,
        label: country.name,
        icon: <Flag code={country.id} className="w-5 h-4 object-cover rounded" />,
        subtitle: country.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setCountries(countryOptions);
  }, []);

  // When country changes, automatically set timezone
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'country' && value.country) {
        const countryData = ct.getAllCountries();
        const country = Object.values(countryData).find((c: Country) => c.name === value.country) as Country | undefined;

        if (country) {
          setValue('countryCode', country.id);

          // Get the primary timezone for the country (first one)
          const timezones = country.timezones;
          if (timezones && timezones.length > 0) {
            const primaryTimezone = timezones[0];
            setSelectedTimezone(primaryTimezone);
            setValue('timezone', primaryTimezone);
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  // Reset form when initialData changes, but only if it's actually different
  const prevInitialDataRef = useRef(initialData);
  useEffect(() => {
    if (skipReset) return;

    // Deep comparison for arrays/objects
    const prevData = prevInitialDataRef.current;
    const dataChanged =
      JSON.stringify(prevData?.keyPeople || []) !== JSON.stringify(initialData?.keyPeople || []) ||
      JSON.stringify(prevData?.brandColors || {}) !== JSON.stringify(initialData?.brandColors || {}) ||
      prevData?.logoUrl !== initialData?.logoUrl ||
      prevData?.brandGuidelines !== initialData?.brandGuidelines ||
      prevData?.country !== initialData?.country ||
      prevData?.countryCode !== initialData?.countryCode ||
      prevData?.timezone !== initialData?.timezone;

    if (!dataChanged && prevData) return;

    prevInitialDataRef.current = initialData;

    reset({
      keyPeople: initialData?.keyPeople || [],
      brandColors: initialData?.brandColors || { primary: '', secondary: '', accent: '' },
      logoUrl: initialData?.logoUrl || '',
      brandGuidelines: initialData?.brandGuidelines || '',
      country: initialData?.country || '',
      countryCode: initialData?.countryCode || '',
      timezone: initialData?.timezone || '',
    });

    if (initialData?.countryCode) {
      const countryData = ct.getAllCountries();
      const country = (countryData as Record<string, Country>)[initialData.countryCode] as Country | undefined;
      if (country && country.timezones && country.timezones.length > 0) {
        setSelectedTimezone(country.timezones[0]);
      }
    }
    // Update logo preview when logoUrl changes
    if (initialData?.logoUrl) {
      // Extract path from URL if it's a full URL (signed URL), otherwise use as-is
      const permanentUrl = extractPathFromUrl(initialData.logoUrl);
      setLogoPermanentUrl(permanentUrl);
      // Use the original URL for preview (might be signed URL for display)
      setLogoPreview(initialData.logoUrl);
    } else if (!initialData?.logoUrl) {
      setLogoPreview('');
      setLogoPermanentUrl('');
    }
  }, [initialData, reset, skipReset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'keyPeople',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file using service
    const validation = uploadService.validateFile(file, 'image', 5);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    try {
      setUploadingLogo(true);
      const { url, signedUrl } = await uploadService.uploadImage(file);
      // Store permanent URL (S3 key) for backend submission
      setLogoPermanentUrl(url || '');
      // Use signedUrl for preview (it's a full URL that works immediately)
      // Use signedUrl for validation too (it's a full URL that passes validation)
      const displayUrl = signedUrl || url || '';
      setValue('logoUrl', displayUrl, { shouldValidate: true });
      setLogoPreview(displayUrl);
      // Trigger validation to clear any previous errors
      trigger('logoUrl');
    } catch (error: unknown) {
      console.error('Logo upload failed:', error);
      const errorMessage = getErrorMessage(error);
      // Set validation error if upload fails
      setValue('logoUrl', '', { shouldValidate: true });
      trigger('logoUrl');
      alert(errorMessage);
    } finally {
      setUploadingLogo(false);
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const onSubmit = async (data: FormData) => {
    // Send permanent URL (S3 key) to backend, not the expiring signedUrl
    // Priority: logoPermanentUrl (from upload) > extract path from data.logoUrl > data.logoUrl
    let logoUrlToSend = logoPermanentUrl;
    if (!logoUrlToSend && data.logoUrl) {
      // Extract path from form value if it's a URL (might be signed URL from initial data)
      logoUrlToSend = extractPathFromUrl(data.logoUrl);
    }

    // Also update user profile with timezone when saving step 2
    if (data.timezone) {
      try {
        await api.put('/users/profile', {
          timezone: data.timezone,
          country: data.country
        });
      } catch (err: unknown) {
        console.error('Failed to update user profile with timezone:', err);
        // Don't block the wizard save if profile update fails
      }
    }

    // Submit with permanent URL (S3 key/path only, never a signed URL)
    onNext({
      ...data,
      logoUrl: logoUrlToSend,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Header */}
      {onBack && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Company Profile</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Add key people and brand identity
          </p>
        </div>
      )}

      {/* Country Selection - Mandatory */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700">Country</label>
          <span className="text-xs text-red-500 font-medium">Required</span>
        </div>
        <Controller
          name="country"
          control={control}
          render={({ field }) => (
            <SearchableSelect
              options={countries}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                const countryData = ct.getAllCountries();
                const country = Object.values(countryData).find((c: Country) => c.name === value) as Country | undefined;
                if (country) {
                  setValue('countryCode', country.id);
                  if (country.timezones && country.timezones.length > 0) {
                    const primaryTimezone = country.timezones[0];
                    setSelectedTimezone(primaryTimezone);
                    setValue('timezone', primaryTimezone);
                  }
                }
              }}
              placeholder="Search for your country..."
              emptyMessage="No countries found"
              error={errors.country?.message}
            />
          )}
        />
        {errors.country && (
          <p className="text-xs text-red-600">{errors.country.message}</p>
        )}
        {selectedTimezone && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-xs font-medium text-blue-900 mb-1">Selected Timezone:</p>
            <p className="text-sm text-blue-700">{selectedTimezone}</p>
            <p className="text-xs text-blue-600 mt-1">
              This timezone will be saved to your profile for scheduled publishing.
            </p>
          </div>
        )}
      </div>

      {/* Key People Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700">Key People</label>
          <span className="text-xs text-slate-400">Optional</span>
        </div>
        {errors.keyPeople && Array.isArray(errors.keyPeople) && errors.keyPeople.some((err) => {
          if (!err) return false;
          // Check if this person has any field errors
          return err.name || err.familyName || err.role || err.linkedinUrl || err.pictureUrl;
        }) && (
            <p className="text-xs text-red-600">Please fix errors in key people fields below</p>
          )}

        {fields.map((field, index) => (
          <div key={field.id} className="p-4 border border-slate-200 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-slate-700">Person {index + 1}</h3>
              <button
                type="button"
                onClick={() => remove(index)}
                className="text-slate-400 hover:text-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Controller
                name={`keyPeople.${index}.name`}
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    type="text"
                    placeholder="Name *"
                    fullWidth
                    error={fieldState.error?.message || errors.keyPeople?.[index]?.name?.message}
                    onChange={(e) => {
                      field.onChange(e.target.value.trimStart());
                    }}
                    onBlur={() => {
                      field.onBlur();
                      // Trim on blur and update
                      if (field.value) {
                        field.onChange(field.value.trim());
                      }
                      trigger(`keyPeople.${index}.name`);
                    }}
                  />
                )}
              />
              <Controller
                name={`keyPeople.${index}.familyName`}
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    type="text"
                    placeholder="Family Name *"
                    fullWidth
                    error={fieldState.error?.message || errors.keyPeople?.[index]?.familyName?.message}
                    onChange={(e) => {
                      field.onChange(e.target.value.trimStart());
                    }}
                    onBlur={() => {
                      field.onBlur();
                      // Trim on blur and update
                      if (field.value) {
                        field.onChange(field.value.trim());
                      }
                      trigger(`keyPeople.${index}.familyName`);
                    }}
                  />
                )}
              />
              <Controller
                name={`keyPeople.${index}.linkedinUrl`}
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    type="url"
                    placeholder="LinkedIn URL (e.g., https://linkedin.com/in/username)"
                    fullWidth
                    error={fieldState.error?.message || errors.keyPeople?.[index]?.linkedinUrl?.message}
                    onChange={(e) => {
                      // Remove @ symbol if present at start and trim
                      let value = e.target.value.trimStart().replace(/^@+/, '');
                      field.onChange(value);
                    }}
                    onBlur={() => {
                      field.onBlur();
                      // Clean and trim on blur
                      if (field.value) {
                        let cleaned = field.value.trim().replace(/^@+/, '');
                        field.onChange(cleaned);
                      }
                      trigger(`keyPeople.${index}.linkedinUrl`);
                    }}
                  />
                )}
              />
              <Controller
                name={`keyPeople.${index}.role`}
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    type="text"
                    placeholder="Role *"
                    fullWidth
                    error={fieldState.error?.message || errors.keyPeople?.[index]?.role?.message}
                    onChange={(e) => {
                      field.onChange(e.target.value.trimStart());
                    }}
                    onBlur={() => {
                      field.onBlur();
                      // Trim on blur and update
                      if (field.value) {
                        field.onChange(field.value.trim());
                      }
                      trigger(`keyPeople.${index}.role`);
                    }}
                  />
                )}
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={() => {
            append({ name: '', familyName: '', linkedinUrl: '', role: '', pictureUrl: '' });
            // Trigger validation after adding new person
            setTimeout(() => {
              trigger('keyPeople');
            }, 0);
          }}
          className="w-full h-9 text-sm font-medium text-slate-600 border border-dashed border-slate-300 rounded-lg 
            hover:border-slate-900 hover:text-slate-900 transition-colors flex items-center justify-center gap-2"
        >
          <span>+</span> Add Person
        </button>
      </div>

      {/* Brand Colors */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700">Brand Colors</label>
          <span className="text-xs text-slate-400">Optional</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {(['primary', 'secondary', 'accent'] as const).map((colorKey) => {
            const labelMap: Record<string, string> = {
              primary: 'primary',
              secondary: 'secondary color',
              accent: 'accent',
            };
            return (
              <Controller
                key={colorKey}
                name={`brandColors.${colorKey}`}
                control={control}
                render={({ field }) => (
                  <ColorPicker
                    label={labelMap[colorKey]}
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                )}
              />
            );
          })}
        </div>
      </div>

      {/* Logo Upload */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700">Company Logo</label>
          <span className="text-xs text-slate-400">Required</span>
        </div>
        <div className="flex items-start gap-4">
          {logoPreview && (
            <div className="w-16 h-16 border border-slate-200 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center shrink-0">
              <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
            </div>
          )}
          <div className={`flex-1 ${errors.logoUrl ? 'space-y-1' : ''}`}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              disabled={uploadingLogo}
              className="hidden"
              id="logo-upload"
            />
            <Button
              type="button"
              variant="secondary"
              size="md"
              disabled={uploadingLogo}
              isLoading={uploadingLogo}
              onClick={handleLogoUploadClick}
              className="flex items-center gap-2"
            >
              {logoPreview ? 'Change Logo' : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload Logo
                </>
              )}
            </Button>
            {errors.logoUrl && (
              <p className="text-sm text-red-600 font-normal">{errors.logoUrl.message}</p>
            )}
            <p className="mt-2 text-xs text-slate-500">PNG, JPG, GIF up to 5MB</p>
          </div>
        </div>
      </div>

      {/* Brand Guidelines */}
      <Controller
        name="brandGuidelines"
        control={control}
        render={({ field }) => (
          <Textarea
            {...field}
            label="Brand Guidelines"
            optional
            rows={3}
            placeholder="Describe your brand guidelines, visual identity..."
            fullWidth
            error={errors.brandGuidelines?.message}
          />
        )}
      />

      {/* Actions */}
      <div className="flex gap-3 pt-6 mt-6 border-t border-slate-200 justify-end">
        {onBack && (
          <Button type="button" variant="secondary" onClick={onBack}>
            Back
          </Button>
        )}
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};
